import React, { useCallback, useState } from "react";
import {
  useUseEndpointActions,
  useUseEndpointValidation,
} from "../../../../contexts";

export const USE_ENDPOINT_MISSING_HELPER = "Required for fetch request.";

/**
 * Extract property name from JSON Forms path or scope
 */
function getPropertyName(
  path: string | undefined,
  scope: string | undefined
): string {
  const fromPath = path?.split(/[/.]/).filter(Boolean).pop() ?? "";
  if (fromPath) return fromPath;
  return scope?.split("/").filter(Boolean).pop() ?? "";
}

/**
 * Convert JSON Forms path to dot notation (e.g., "#/properties/portfolio/properties/account" -> "portfolio.account")
 */
function getPathDot(path: string | undefined): string {
  return path?.replace(/^#\/properties\//, "").replace(/\//g, ".") ?? "";
}

export interface UseEndpointControlParams {
    path: string | undefined;
    uischema: unknown;
    schema: unknown;
    errors: unknown;
    handleChange: (path: string, value: unknown) => void;
    /** Current form data (e.g. from useJsonForms().core.data) for the use_endpoint request. */
    formData: unknown;
}

export interface UseEndpointControlResult {
    propertyName: string;
    pathDot: string;
    useEndpoint: string | undefined;
    isMissingForEndpoint: boolean;
    showError: boolean;
    /** For TextField helperText (string or array of strings from jsonforms). */
    helperText: React.ReactNode;
    handleFetchClick: () => Promise<void>;
    isFetching: boolean;
    hasUseEndpoint: boolean;
}

/**
 * Shared hook for RPC form controls that support use_endpoint (e.g. NumberControl, TextControl).
 * Centralizes property/path resolution, validation state, and fetch-with-loading behavior.
 *
 * This hook:
 * - Extracts the property name and path from JSON Forms props
 * - Checks if the field is missing for the current endpoint validation
 * - Provides a fetch handler with loading state
 * - Manages error display and helper text
 *
 * @example
 * ```tsx
 * const {
 *   useEndpoint,
 *   showError,
 *   helperText,
 *   handleFetchClick,
 *   isFetching,
 *   hasUseEndpoint,
 * } = useEndpointControl({
 *   path,
 *   uischema,
 *   schema,
 *   errors,
 *   handleChange,
 *   formData,
 * });
 * ```
 */
export function useEndpointControl({
  path,
  uischema,
  schema,
  errors,
  handleChange,
  formData,
}: UseEndpointControlParams): UseEndpointControlResult {
  const [isFetching, setIsFetching] = useState(false);
  const { callUseEndpoint } = useUseEndpointActions();
  const { missingPathsSet } = useUseEndpointValidation();

  // Extract property name and path from JSON Forms props
  const scope =
    typeof uischema === "object" && uischema !== null && "scope" in uischema
      ? (uischema as { scope?: string }).scope
      : undefined;
  const propertyName = getPropertyName(path, scope);
  const pathDot = getPathDot(path);
  const useEndpoint = (schema as { use_endpoint?: string })?.use_endpoint;

  // Check validation state
  const isMissingForEndpoint = missingPathsSet?.has(pathDot) ?? false;
  const showError = Array.isArray(errors) ? errors.length > 0 : Boolean(errors);
  const resolvedShowError = showError || isMissingForEndpoint;

  // Determine helper text: show validation message if missing, otherwise show JSON Forms errors
  const helperText: React.ReactNode =
    isMissingForEndpoint && !showError
      ? USE_ENDPOINT_MISSING_HELPER
      : (errors as React.ReactNode);

  /**
   * Handle fetch button click
   * Calls the use_endpoint RPC and updates the field value on success
   */
  const handleFetchClick = useCallback(async () => {
    if (!useEndpoint || !callUseEndpoint || formData == null) return;
    setIsFetching(true);
    try {
      const result = await callUseEndpoint(
        useEndpoint,
        formData as Record<string, unknown>
      );
      if (
        result.success &&
        result.data != null &&
        typeof result.data === "object" &&
        propertyName in result.data &&
        path != null
      ) {
        handleChange(
          path,
          (result.data as Record<string, unknown>)[propertyName]
        );
      }
    } finally {
      setIsFetching(false);
    }
  }, [
    useEndpoint,
    callUseEndpoint,
    formData,
    propertyName,
    path,
    handleChange,
  ]);

  return {
    propertyName,
    pathDot,
    useEndpoint,
    isMissingForEndpoint,
    showError: resolvedShowError,
    helperText,
    handleFetchClick,
    isFetching,
    hasUseEndpoint: Boolean(useEndpoint && callUseEndpoint),
  };
}
